// Searchable multi-select filter — the shared building block behind the v2
// "search" filter bars (Services directory, Jobs catalog). Type to narrow the
// options, tick several, and they render as hue-tinted chips.

import React from 'react';
import { Autocomplete, Checkbox, Chip, SxProps, TextField, Theme, Typography } from '@mui/material';
import {
  Search as SearchIcon,
  CheckBoxOutlineBlank as UncheckedIcon,
  CheckBox as CheckedIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getColors } from '../theme/tokens';

const checkboxIcon = <UncheckedIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckedIcon fontSize="small" />;

export interface MultiSelectFilterProps {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Chip tint (e.g. APP_HUE / JOB_HUE). */
  hue: string;
  placeholder: string;
  sx?: SxProps<Theme>;
  /**
   * Recovers a display label from an option value — for callers whose
   * options carry extra identity baked in (e.g. a type-qualified
   * serviceFilterKey like "ecs:ecs") that shouldn't be shown verbatim.
   * Defaults to the identity function, so every caller that doesn't pass
   * this is unaffected.
   */
  getOptionLabel?: (option: string) => string;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  options,
  value,
  onChange,
  hue,
  placeholder,
  sx,
  getOptionLabel = (option: string) => option,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);

  const tagChipSx = {
    height: 22,
    fontWeight: 600,
    fontSize: '0.72rem',
    backgroundColor: `${hue}14`,
    color: theme.palette.mode === 'dark' ? c.ink : hue,
    border: `1px solid ${hue}44`,
  };

  return (
    <Autocomplete<string, true>
      multiple
      disableCloseOnSelect
      size="small"
      options={options}
      value={value.filter(v => options.includes(v))}
      onChange={(_, next) => onChange(Array.from(new Set(next)))}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option}>
          <Checkbox
            size="small"
            icon={checkboxIcon}
            checkedIcon={checkboxCheckedIcon}
            checked={selected}
            sx={{ mr: 1, p: 0.5 }}
          />
          <Typography variant="body2" noWrap>
            {getOptionLabel(option)}
          </Typography>
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip key={key} {...tagProps} label={getOptionLabel(option)} size="small" sx={tagChipSx} />
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ fontSize: 17, color: c.inkFaint, ml: 0.5, mr: 0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={sx ?? { minWidth: 240, maxWidth: 420, flexGrow: 1 }}
    />
  );
};

export default MultiSelectFilter;
